import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {CommentBox, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import type {Subject} from '@github-ui/comment-box/subject'
import {ContributorFooter} from '@github-ui/contributor-footer/ContributorFooter'
import {COPILOT_PATH} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {useCopilotGeneratePanel} from '@github-ui/copilot-generate-description-panel/use-copilot-generate-panel'
import {validateIssueBody, validateIssueTitle} from '@github-ui/entity-validators'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {getIssueFieldValue, toIssueFieldType} from '@github-ui/issue-fields-shared/issue-field'
import type {IssueFormGraphqlElement} from '@github-ui/issue-form/create-issue-query-types'
import {IssueFormElements} from '@github-ui/issue-form/IssueFormElements'
import type {ChangeTrackingProps, IssueFormElementRef, IssueFormRef} from '@github-ui/issue-form/Types'
import {getIssueFieldSelectionValue} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import type {RepositoryPickerRepository} from '@github-ui/issues-repository-picker'
import {MarkdownEditor, type ViewMode} from '@github-ui/markdown-editor'
import type {CreateIssueInput} from '@github-ui/server-persisted-queries/types/createIssueMutation'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/use-should-show-copilot-functionality-in-issues'
import {AlertIcon, ArrowUpRightIcon, CopilotIcon, PencilAiIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Banner, Dialog, Flash} from '@primer/react'
import {AriaAlert} from '@primer/react/experimental'
import {clsx} from 'clsx'
import React, {
  type FormEvent,
  type RefObject,
  Suspense,
  useCallback,
  useEffect,
  useEffectEvent,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import {ERRORS} from './constants/errors'
import {LABELS} from './constants/labels'
import {TEMP_SUB_ISSUE_ID_PREFIX} from './constants/values'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {useIssueCreateComposition, useIssueCreateMetadata} from './contexts/IssueCreateDataContext'
import {CopilotGenerateDescriptionDialog} from './CopilotGenerateDescriptionDialog'
import styles from './CreateIssueForm.module.css'
import {CreateIssueFormTitle} from './CreateIssueFormTitle'
import type {DuplicateDetectionState} from './hooks/use-duplicate-detection'
import {type IssueCreateStatus, useIssueFormSecondaryQuery} from './hooks/use-issue-form-secondary-query'
import {useSemanticSearchAnalytics} from './hooks/use-semantic-search-analytics'
import {MetadataSelectors} from './metadata/MetadataSelectors'
import {useCreateIssueAddBlockedByMutation} from './mutations/use-create-issue-add-blocked-by-mutation'
import {useCreateIssueMutation} from './mutations/use-create-issue-mutation'
import {DisplayMode} from './utils/display-mode'
import {
  type CreatedIssue,
  instanceOfIssueFormData,
  type IssueCreatePayload,
  IssueCreationKind,
  type OnCreateProps,
  type SubIssueOptimisticCreate,
} from './utils/model'
import {type AddBlockedBy, handleIssueRelationships} from './utils/relationship-helpers'
import {resolveIssueStatus} from './utils/resolve-issue-status'
import {getPotentialFormDefaultValuesFromUrl} from './utils/urls'

const CopilotGenerateDescriptionPanel = React.lazy(async () => {
  const mod = await import('@github-ui/copilot-generate-description-panel')
  return {default: mod.CopilotGenerateDescriptionPanel}
})

export type CreateIssueCallbackProps = {
  onCreateSuccess: ({issue, createMore}: OnCreateProps) => void
  onCreateError: (error: Error) => void
  onCancel: () => void
  onBeforeCreate?: (issueBody: string) => Promise<string>
  onSubIssueOptimisticCreate?: (create: SubIssueOptimisticCreate) => void
} & ChangeTrackingProps

const EMPTY_ORG_PINNED_FIELDS: readonly IssueFieldSelection[] = []
const EMPTY_FIELD_VISIBILITY_MAP: Record<string, string> = {}
const EMPTY_OPEN_STATUSES: readonly IssueCreateStatus[] = []

export type CreateIssueFormProps = {
  repository: RepositoryPickerRepository
  selectedTemplate?: IssueCreatePayload
  title: string
  setTitle: (title: string) => void
  body: string
  setBody: (body: string) => void
  clearOnCreate: () => void
  focusTitleInput?: boolean
  footer?: React.ReactElement
  issueFormRef: RefObject<IssueFormRef | null>
  defaultValuesById?: Record<string, string>
  showUserRestrictedView?: boolean
  restrictedViewContent?: React.ReactElement
  /** Duplicate detection state from useDuplicateDetection hook */
  duplicateDetection?: DuplicateDetectionState
} & CreateIssueCallbackProps

export const CreateIssueForm = ({
  repository,
  title,
  setTitle,
  body,
  setBody,
  clearOnCreate,
  onCreateSuccess,
  onCreateError,
  onSubIssueOptimisticCreate,
  selectedTemplate,
  footer,
  issueFormRef,
  onBeforeCreate,
  defaultValuesById,
  showUserRestrictedView = false,
  restrictedViewContent,
  onFieldChange,
  duplicateDetection,
}: CreateIssueFormProps): React.ReactElement => {
  const {
    optionConfig,
    createMore,
    createMoreCreatedPath,
    setCreateMoreCreatedPath,
    isSubmitting,
    setIsSubmitting,
    onCreateAction,
    setIsFileUploading,
    displayMode,
    isCreatingSubIssueFromProject,
  } = useIssueCreateConfigContext()
  const showMetadataSidepanel = !optionConfig.insidePortal
  const titleInputRef = useRef<HTMLInputElement>(null)
  const isSubmissionInProgressRef = useRef(false)
  const setSubmissionInProgress = useCallback(
    (value: boolean) => {
      isSubmissionInProgressRef.current = value
      setIsSubmitting(value)
    },
    [setIsSubmitting],
  )

  // Lazily generate clientMutationId for idempotency to prevent duplicate issue creation
  const clientMutationIdRef = useRef<string | undefined>(undefined)

  const [viewMode, setViewMode] = useState<ViewMode>()
  const [titleValidationResult, setTitleValidationResult] = useState<string | undefined>(undefined)
  const [bodyValidationResult, setBodyValidationResult] = useState<string | undefined>(undefined)
  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)
  const secondaryIssueFormData = useIssueFormSecondaryQuery({
    owner: repository.owner.login,
    repo: repository.name,
  })
  const errorBanner = useRef<HTMLDivElement>(null)
  const {mutateAsync: createIssueMutateAsync} = useCreateIssueMutation()
  const {mutateAsync: createIssueAddBlockedByMutateAsync} = useCreateIssueAddBlockedByMutation()
  const addBlockedBy = useCallback<AddBlockedBy>(
    (input, onRequestStart) => createIssueAddBlockedByMutateAsync({input, onRequestStart}),
    [createIssueAddBlockedByMutateAsync],
  )
  const {sendAnalyticsEvent} = useAnalytics()
  const {addToast} = useToastContext()
  const {
    labels,
    assignees,
    replaceAssignees,
    projects,
    milestone,
    issueType,
    issueStatus,
    issueFields,
    parentIssueId,
    blockedByIssueIds,
    blockingIssueIds,
    usedStorageKeyPrefix,
    isCopilotAssignmentDialogOpen,
    setIsCopilotAssignmentDialogOpen,
    copilotAssignmentAttributes,
    setCopilotAssignmentAttributes,
  } = useIssueCreateMetadata()
  const {setIssueFormBody} = useIssueCreateComposition()

  // Extract duplicate detection callbacks (may be undefined if feature disabled)
  const resetDuplicateDetection = duplicateDetection?.resetForNewSession
  const trackDuplicateDetectionIssueCreated = duplicateDetection?.trackIssueCreated

  const shouldShowCopilotFunctionality = useShouldShowCopilotFunctionalityInIssues({
    additionalConditions: [selectedTemplate ? !instanceOfIssueFormData(selectedTemplate.data) : true],
    repositoryVisibility: repository.visibility,
    userCanPushToRepo: repository.viewerCanPush,
  })

  const postSemanticSearchAnalytics = useSemanticSearchAnalytics(repository)

  useEffect(() => {
    if (shouldShowCopilotFunctionality) {
      sendAnalyticsEvent('analytics.view', 'ISSUE_CREATE_NEW_ISSUE_WITH_COPILOT_BUTTON_VIEW', {
        repoNWO: `${repository.owner.login}/${repository.name}`,
      })
    }
  }, [repository.name, repository.owner.login, sendAnalyticsEvent, shouldShowCopilotFunctionality])

  // When Copilot assignment attributes are set after modal submission, trigger issue creation
  const pendingCreationRef = useRef(false)
  const onCopilotAssignmentAttributesSet = useEffectEvent(() => {
    if (copilotAssignmentAttributes && pendingCreationRef.current) {
      pendingCreationRef.current = false
      // Trigger the issue creation now that we have the Copilot attributes
      onCreateAction.current?.onCreate(false, false)
    }
  })
  useEffect(() => {
    onCopilotAssignmentAttributesSet()
  }, [copilotAssignmentAttributes])

  // Reset pendingCreationRef when modal closes without submission
  useEffect(() => {
    if (!isCopilotAssignmentDialogOpen && !copilotAssignmentAttributes) {
      pendingCreationRef.current = false
    }
  }, [isCopilotAssignmentDialogOpen, copilotAssignmentAttributes])

  const canIssueType = repository.viewerIssueCreationPermissions.typeable
  const canSetIssueFields = repository.viewerIssueCreationPermissions.canSetIssueFields

  // When the owner has no issue fields, or the org has no issue types, the corresponding editors are hidden
  // (see MetadataFooter/MetadataSidebar). Default to `true` while the secondary
  // query is still loading so we don't strip prefilled metadata on a fast submit.
  const orgHasIssueTypes = secondaryIssueFormData?.orgHasIssueTypes ?? true
  const ownerHasIssueFields = secondaryIssueFormData?.ownerHasIssueFields ?? true
  const openStatuses = secondaryIssueFormData?.openStatuses ?? EMPTY_OPEN_STATUSES
  const statusId = resolveIssueStatus(openStatuses, issueStatus)?.id

  const formatFormErrorMessage = (errors: Error[]) => {
    const errorMessages = errors
      .map(err => err?.message)
      .filter(Boolean)
      .join(', ')

    // Some gql errors start with lowercase letters, so normalizing it
    const cleanedErrorMessages = errorMessages ? errorMessages.charAt(0).toUpperCase() + errorMessages.slice(1) : null

    return cleanedErrorMessages ?? ERRORS.createIssueError
  }

  const selectedDefaultValuesById = useMemo(
    () => defaultValuesById || getPotentialFormDefaultValuesFromUrl(),
    [defaultValuesById],
  )

  useEffect(() => {
    if (submissionErrors && submissionErrors.length > 0 && errorBanner?.current) {
      errorBanner.current.focus()
    }
  }, [errorBanner, submissionErrors])

  // Duplicate detection feature - get state from prop
  const isDuplicateDetectionEnabled = duplicateDetection?.isDuplicateDetectionEnabled ?? false
  const isIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)

  const handleFormFieldChangeForDuplicateDetection = useCallback<NonNullable<ChangeTrackingProps['onFieldChange']>>(
    (id, indexOfFormElement, value) => {
      onFieldChange?.(id, indexOfFormElement, value)

      if (isDuplicateDetectionEnabled && issueFormRef.current) {
        queueMicrotask(() => {
          setIssueFormBody(issueFormRef.current?.markdown() ?? '')
        })
      }
    },
    [isDuplicateDetectionEnabled, issueFormRef, setIssueFormBody, onFieldChange],
  )

  useImperativeHandle(
    onCreateAction,
    () => ({
      onCreate: async (isSubmittingArg, createMoreArg) => {
        if (isSubmittingArg || isSubmissionInProgressRef.current) return

        let refToFocusOnError: HTMLInputElement | IssueFormElementRef | undefined = undefined

        const inputValidationResult = validateIssueTitle(title)
        setTitleValidationResult(inputValidationResult.errorMessage)
        if (!inputValidationResult.isValid && titleInputRef.current) {
          refToFocusOnError = titleInputRef.current
        }

        const isUsingIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)
        let issueBodyToSave = body
        if (isUsingIssueForm && issueFormRef.current) {
          const invalidInputs = issueFormRef.current.getInvalidInputs()
          // If there are validation errors, use first input if there's no ref yet
          if (invalidInputs.length > 0) {
            refToFocusOnError = refToFocusOnError || invalidInputs[0]
          } else {
            issueBodyToSave = issueFormRef.current.markdown() ?? ''
          }
        }

        // We validate the length === 0 also for the initial state, in which the validation result will be undefined, too.
        if (
          refToFocusOnError ||
          titleValidationResult !== undefined ||
          (title ?? '').trim().length === 0 ||
          repository === undefined ||
          bodyValidationResult !== undefined
        ) {
          // If there is an input with an error, focus it
          refToFocusOnError?.focus()
          return
        }

        setSubmissionErrors(null)
        setSubmissionInProgress(true)

        // Check if Copilot is one of the assignees but we don't have assignment attributes yet
        const hasCopilotAssignee = assignees.some(assignee => isCopilot(assignee.login))
        if (hasCopilotAssignee && !copilotAssignmentAttributes) {
          // Show the Copilot assignment modal and return - issue will be created after modal submission
          pendingCreationRef.current = true
          setIsCopilotAssignmentDialogOpen(true)
          setSubmissionInProgress(false)
          return
        }

        if (onBeforeCreate) {
          issueBodyToSave = await onBeforeCreate(issueBodyToSave)
        }

        let issueTemplate = undefined
        if (
          selectedTemplate &&
          (selectedTemplate.kind === IssueCreationKind.IssueTemplate ||
            selectedTemplate.kind === IssueCreationKind.IssueForm)
        ) {
          issueTemplate = selectedTemplate.name
        }

        const input = {
          repositoryId: repository.id,
          title: title ? title.trim() : '',
          body: issueBodyToSave,
          labelIds: labels.length > 0 ? labels.map(label => label.id) : undefined,
          assigneeIds: assignees.length > 0 ? assignees.map(assignee => assignee.id) : undefined,
          milestoneId: milestone?.id,
          issueTypeId: (canIssueType || issueTemplate) && orgHasIssueTypes ? issueType?.id : null,
          statusId,
          issueFields:
            canSetIssueFields && ownerHasIssueFields
              ? (issueFields
                  .map(field => {
                    if (field.field.dataType === undefined || field.field.id === undefined) return null
                    const value = getIssueFieldSelectionValue(toIssueFieldType(field.field.dataType), field.value)
                    return {
                      fieldId: field.field.id,
                      ...getIssueFieldValue(toIssueFieldType(field.field.dataType), value),
                    }
                  })
                  // .filter doesn't narrow away null
                  .filter(v => !!v) as CreateIssueInput['issueFields'])
              : null,
          issueTemplate,
          isDuplicated: displayMode === DisplayMode.IssueDuplication,
          parentIssueId,
          projectV2Ids: projects.length > 0 ? projects.map(project => project.id) : undefined,
          // Lazily generate clientMutationId on first use (client-side only) to avoid SSR issues
          clientMutationId: (clientMutationIdRef.current ??= crypto.randomUUID()),
          // CopilotAssignmentAttributes uses optional fields vs generated nullable fields
          copilotAssignment: (copilotAssignmentAttributes ?? undefined) as CreateIssueInput['copilotAssignment'],
        }

        // For sub-issues, close the dialog immediately (optimistic update will show the item)
        // For regular issues, wait for the mutation to complete before calling onCreateSuccess
        // For sub-issues created from a project, wait for the mutation to complete so the issue can be added to the project
        const closeImmediately = !isCreatingSubIssueFromProject && !!parentIssueId && !createMoreArg

        const subIssueParentId = parentIssueId ?? undefined
        const subIssueTempId =
          onSubIssueOptimisticCreate && parentIssueId ? `${TEMP_SUB_ISSUE_ID_PREFIX}${crypto.randomUUID()}` : undefined

        const handleCreateError = (error: Error) => {
          reportError(formatError(error.message))
          onCreateError(error)
          setSubmissionInProgress(false)
          let errorMessage = ERRORS.createIssueError

          if (error.cause && Array.isArray(error.cause) && error.cause.length > 0) {
            errorMessage = formatFormErrorMessage(error.cause)
          }

          // For sub-issues that closed immediately, show a toast since the dialog is gone
          if (closeImmediately) {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: errorMessage,
            })
          } else {
            setSubmissionErrors(errorMessage)
          }
        }

        const handleCreateSuccess = (newIssue: CreatedIssue) => {
          // Release the submission mutex only when the form stays mounted and interactive, i.e. the
          // "create more" flow. When create-more is disabled this is a terminal submission: the
          // full-page route navigates away and the dialog closes. Holding the mutex until the form
          // unmounts stops a repeated Create activation during that async window from creating a
          // duplicate issue. The error and Copilot-deferral paths release the mutex separately.
          if (createMoreArg) {
            setSubmissionInProgress(false)
          }

          sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_NEW_ISSUE_BUTTON', {
            issueId: newIssue.id,
            issueNumber: newIssue.number,
            issueNWO: `${newIssue.repository.owner.login}/${newIssue.repository.name}`,
          })

          // Track duplicate detection interaction when issue is created
          if (isDuplicateDetectionEnabled) {
            trackDuplicateDetectionIssueCreated?.(newIssue.id, newIssue.number, title.trim(), issueBodyToSave)
          }

          const hasIssueRelationships = blockedByIssueIds.length > 0 || blockingIssueIds.length > 0
          let relationshipRequestsStarted: Promise<void> | undefined
          if (hasIssueRelationships) {
            const {promise, resolve} = Promise.withResolvers<void>()
            relationshipRequestsStarted = promise
            void handleIssueRelationships(addBlockedBy, newIssue.id, blockedByIssueIds, blockingIssueIds, resolve)
          }

          // Generate a new clientMutationId for the next submission to ensure idempotency
          clientMutationIdRef.current = crypto.randomUUID()

          if (createMoreArg) {
            setViewMode('edit')
            setCreateMoreCreatedPath({...createMoreCreatedPath, number: newIssue.number})

            if (isDuplicateDetectionEnabled) {
              resetDuplicateDetection?.()
              setIssueFormBody('') // Clear stale form body to prevent incorrect duplicate suggestions
            }
          }

          // Only call onCreateSuccess here if we didn't already close immediately
          if (!closeImmediately) {
            const notifyCreateSuccess = () => onCreateSuccess({issue: newIssue, createMore: createMoreArg})
            if (relationshipRequestsStarted) {
              void (async () => {
                await relationshipRequestsStarted
                notifyCreateSuccess()
              })()
            } else {
              notifyCreateSuccess()
            }
          }
        }

        try {
          const createIssuePromise = createIssueMutateAsync(
            // fetchParent triggers @include(if: $fetchParent) in the GraphQL query to return parent summary
            {
              input,
              fetchParent: !!input.parentIssueId,
              operation: subIssueParentId
                ? {
                    type: 'deltaEntityFieldValues',
                    id: subIssueParentId,
                    field: 'subIssuesSummary',
                    deltas: {total: 1},
                  }
                : undefined,
              operationId: subIssueParentId ? `${subIssueTempId ?? crypto.randomUUID()}:parent` : undefined,
            },
          )
          if (subIssueTempId) {
            onSubIssueOptimisticCreate?.({tempId: subIssueTempId, title: title.trim(), settled: createIssuePromise})
          }
          void (async () => {
            let newIssue: CreatedIssue
            try {
              newIssue = await createIssuePromise
            } catch (error) {
              handleCreateError(error instanceof Error ? error : new Error('Failed to create issue'))
              return
            }

            // Keep consumer callback errors distinct from mutation failures.
            handleCreateSuccess(newIssue)
          })()
        } catch (error) {
          setSubmissionInProgress(false)
          throw error
        }

        // For sub-issues, close immediately without waiting for the mutation to complete
        if (closeImmediately) {
          clearOnCreate()
          // Pass a minimal issue object since the dialog just needs to close
          // The optimistic update already shows the item in the list
          onCreateSuccess({
            issue: {
              id: clientMutationIdRef.current ?? '',
              number: 0,
              repository: {
                name: repository.name,
                owner: {id: '', login: repository.owner.login},
                databaseId: null,
                id: '',
              },
              databaseId: null,
              title: '',
              url: '',
            },
            createMore: false,
          })
        }
      },
    }),
    [
      title,
      selectedTemplate,
      body,
      issueFormRef,
      titleValidationResult,
      repository,
      bodyValidationResult,
      setSubmissionInProgress,
      onBeforeCreate,
      labels,
      assignees,
      milestone?.id,
      canIssueType,
      orgHasIssueTypes,
      issueType?.id,
      statusId,
      canSetIssueFields,
      ownerHasIssueFields,
      issueFields,
      displayMode,
      parentIssueId,
      blockedByIssueIds,
      blockingIssueIds,
      copilotAssignmentAttributes,
      setIsCopilotAssignmentDialogOpen,
      createIssueMutateAsync,
      addBlockedBy,
      onCreateError,
      sendAnalyticsEvent,
      projects,
      onCreateSuccess,
      onSubIssueOptimisticCreate,
      setCreateMoreCreatedPath,
      createMoreCreatedPath,
      clearOnCreate,
      addToast,
      isCreatingSubIssueFromProject,
      isDuplicateDetectionEnabled,
      resetDuplicateDetection,
      trackDuplicateDetectionIssueCreated,
      setIssueFormBody,
    ],
  )

  const handleTitleChange = (e: FormEvent<HTMLInputElement>) => {
    const inputTitle = e.currentTarget.value
    validateAndSetTitle(inputTitle)
  }

  const validateAndSetTitle = (newTitle: string) => {
    const inputValidationResult = validateIssueTitle(newTitle)
    // when title changes, check if we need to clear validation error
    // validation error are only added when user tries to create the issue
    if (inputValidationResult.isValid) {
      setTitleValidationResult(undefined)
    }
    setTitle(newTitle)
    postSemanticSearchAnalytics(newTitle, body)
  }
  const handleBodyChange = (inputBody: string) => {
    validateAndSetBody(inputBody)
  }
  const validateAndSetBody = (newBody: string) => {
    const inputValidationResult = validateIssueBody(newBody)

    setBodyValidationResult(inputValidationResult.errorMessage)
    setBody(newBody)

    postSemanticSearchAnalytics(title, newBody)
  }

  const subject = useMemo<Subject | undefined>(() => {
    if (repository) {
      return {
        type: 'issue',
        repository: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          databaseId: repository.databaseId!,
          nwo: `${repository.owner.login}/${repository.name}`,
          slashCommandsEnabled: repository.slashCommandsEnabled,
        },
      }
    }
  }, [repository])

  const validationErrorId = useId()

  const footerButtons = []
  const navigate = useNavigate()
  const copilotCTARef = useRef<HTMLButtonElement>(null)
  const assigneesButtonRef = useRef<HTMLButtonElement | null>(null)
  const commentBoxRef = useRef<CommentBoxHandle>(null)
  const [isCopilotCTADialogOpen, setIsCopilotCTADialogOpen] = useState(false)
  const [isCopilotGenerateDialogOpen, setIsCopilotGenerateDialogOpen] = useState(false)

  // Determines if we should show the footer button to open a dialog to edit with copilot. Outdated functionality
  // based on another teams work. When we staff-ship write_issue_body_with_copilot, we can remove this.
  const isCreateIssueDescriptionWithCopilotEnabled = isFeatureEnabled('create_issue_description_with_copilot')
  const shouldUseDialogCopilotWriting = isCreateIssueDescriptionWithCopilotEnabled && shouldShowCopilotFunctionality

  // Use the Copilot panel hook to manage inline copilot writing functionality
  const {
    isPanelOpen: isCopilotGeneratePanelOpen,
    isGenerating: isCopilotGenerating,
    shouldShowCopilot: shouldUseInlineCopilotWriting,
    togglePanel: toggleCopilotPanel,
    handleInputFocus: handleCommentBoxInputFocus,
    commands: copilotPanelCommands,
    panelProps: copilotPanelProps,
    containerStyle: copilotContainerStyle,
  } = useCopilotGeneratePanel({
    editorRef: commentBoxRef,
    body,
    onChange: (newBody, newTitle) => {
      validateAndSetBody(newBody)
      if (newTitle !== undefined) {
        validateAndSetTitle(newTitle)
      }
    },
    shouldShowCopilotFunctionality,
    repoOwner: repository.owner.login,
    repoName: repository.name,
    title,
    generateTitle: true,
    commandId: 'issue-create:open-copilot-panel',
    canToggle: viewMode !== 'preview',
  })

  useEffect(() => {
    const assigneesButton = document.getElementById('assignees-select-menu') as HTMLButtonElement | null
    if (assigneesButton) {
      assigneesButtonRef.current = assigneesButton
    }
  }, [])

  const navigateToCopilot = () => {
    sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_NEW_ISSUE_WITH_COPILOT_BUTTON', {
      repoNWO: `${repository.owner.login}/${repository.name}`,
    })
    const prompt = `Create an issue in ${repository.nameWithOwner} to `
    const params = new URLSearchParams({prompt})
    navigate(`${COPILOT_PATH}?${params.toString()}`)
  }

  const handleGenerateDescription = (generatedTitle: string, generatedDescription: string) => {
    setTitle(generatedTitle)
    setBody(generatedDescription)
    setIsCopilotGenerateDialogOpen(false)
  }

  const handleCTAOnClick = () => {
    if (shouldUseInlineCopilotWriting) {
      toggleCopilotPanel()
    } else if (isCreateIssueDescriptionWithCopilotEnabled) {
      setIsCopilotGenerateDialogOpen(true)
    } else {
      if (body.length || title.length) {
        setIsCopilotCTADialogOpen(true)
      } else {
        navigateToCopilot()
      }
    }
  }

  // If not using the panel, we need the footer button on the editor
  if (shouldShowCopilotFunctionality && !shouldUseInlineCopilotWriting) {
    footerButtons.push(
      <MarkdownEditor.FooterButton
        ref={copilotCTARef}
        key="create-issue-with-copilot"
        variant="invisible"
        leadingVisual={shouldUseDialogCopilotWriting ? CopilotIcon : ArrowUpRightIcon}
        size="small"
        className={styles.footerButton}
        onClick={() => {
          handleCTAOnClick()
        }}
      >
        {LABELS.copilotCTAButton}
      </MarkdownEditor.FooterButton>,
    )
  }

  const onCopilotModalSubmit = (
    repositoryId: string,
    branch?: string,
    instructions?: string,
    selectedAgent?: SelectedAgent,
    model?: string,
  ) => {
    setCopilotAssignmentAttributes({
      targetRepositoryId: repositoryId,
      baseRef: branch,
      customInstructions: instructions,
      customAgent: selectedAgent?.subAgentSlug,
      model,
    })

    // Update the assignee to reflect the selected agent instead of always showing "Copilot"
    if (selectedAgent?.displayName && selectedAgent?.login && selectedAgent?.botGlobalRelayId) {
      const isDefaultCopilot = isCopilot(selectedAgent.login)
      if (!isDefaultCopilot) {
        const agentAssignee: (typeof assignees)[number] = {
          id: selectedAgent.botGlobalRelayId ?? '',
          login: selectedAgent.login,
          name: selectedAgent.displayName,
          avatarUrl: selectedAgent.avatarUrl ?? '',
          __typename: 'Bot',
          isCopilot: false,
          isAgent: true,
          displayName: selectedAgent.displayName,
        }
        const updatedAssignees = assignees.map(a => (isCopilot(a.login) ? agentAssignee : a))
        replaceAssignees(updatedAssignees)
      }
    }
  }

  const mainContent = showUserRestrictedView ? (
    restrictedViewContent
  ) : (
    <>
      {isCopilotCTADialogOpen && (
        <Dialog
          title={LABELS.copilotCTADialogTitle}
          onClose={() => setIsCopilotCTADialogOpen(false)}
          footerButtons={[
            {
              buttonType: 'default',
              content: LABELS.copilotCTADialogCancelButton,
              onClick: () => setIsCopilotCTADialogOpen(false),
            },
            {
              buttonType: 'primary',
              content: LABELS.copilotCTADialogContinueButton,
              onClick: navigateToCopilot,
            },
          ]}
          returnFocusRef={copilotCTARef}
        >
          {LABELS.copilotCTADialogDescription}
        </Dialog>
      )}
      {!shouldUseInlineCopilotWriting && isCreateIssueDescriptionWithCopilotEnabled && (
        <CopilotGenerateDescriptionDialog
          isOpen={isCopilotGenerateDialogOpen}
          onClose={() => setIsCopilotGenerateDialogOpen(false)}
          onGenerate={handleGenerateDescription}
          repoOwner={repository.owner.login}
          repoName={repository.name}
          title={title}
          body={body}
          returnFocusRef={copilotCTARef}
        />
      )}
      {submissionErrors && (
        <Banner
          ref={errorBanner}
          title="Error"
          description={<AriaAlert>{submissionErrors}</AriaAlert>}
          variant="critical"
          className={styles.errorBanner}
          role="alert"
        />
      )}
      <CopilotAssignmentModal
        isOpen={isCopilotAssignmentDialogOpen}
        onClose={() => setIsCopilotAssignmentDialogOpen(false)}
        onSubmit={onCopilotModalSubmit}
        repository={{
          name: repository.name,
          owner: {
            login: repository.owner.login,
          },
        }}
        returnFocusRef={assigneesButtonRef}
      />
      <CreateIssueFormTitle
        title={title}
        titleInputRef={titleInputRef}
        handleTitleChange={handleTitleChange}
        titleValidationResult={titleValidationResult}
      />
      {subject && !isIssueForm && (
        <ScopedCommands commands={copilotPanelCommands}>
          <div className={styles.commentBox} style={copilotContainerStyle}>
            <CommentBox
              ref={commentBoxRef}
              subject={subject}
              label={LABELS.issueCreateBodyLabel}
              showLabel
              placeholder={LABELS.issueBodyPlaceholder}
              viewMode={viewMode}
              onChangeViewMode={setViewMode}
              value={body}
              onSave={() => onCreateAction?.current?.onCreate(isSubmitting, createMore)}
              onChange={handleBodyChange}
              saveButtonTrailingIcon={false}
              minHeightLines={optionConfig.insidePortal ? 14 : 20}
              aria-describedby={bodyValidationResult ? validationErrorId : undefined}
              setIsFileUploading={setIsFileUploading}
              toolbarButtons={
                shouldUseInlineCopilotWriting ? (
                  <MarkdownEditor.ToolbarButton
                    variant="invisible"
                    icon={PencilAiIcon}
                    size="small"
                    className={styles.copilotWriteButton}
                    onClick={toggleCopilotPanel}
                    aria-pressed={isCopilotGeneratePanelOpen}
                    aria-label={LABELS.copilotCTAButton}
                  />
                ) : null
              }
              footerButtons={footerButtons}
              disabled={isCopilotGenerating}
              generatingText={isCopilotGenerating}
              onInputFocus={handleCommentBoxInputFocus}
            />
            {shouldUseInlineCopilotWriting && isCopilotGeneratePanelOpen && viewMode !== 'preview' && (
              <Suspense fallback={null}>
                <CopilotGenerateDescriptionPanel {...copilotPanelProps} />
              </Suspense>
            )}
            {bodyValidationResult && (
              <Flash variant="danger" id={validationErrorId}>
                <AlertIcon />
                {bodyValidationResult}
              </Flash>
            )}
          </div>
        </ScopedCommands>
      )}
      {subject && isIssueForm && instanceOfIssueFormData(selectedTemplate.data) && (
        <IssueFormElements
          outputRef={issueFormRef}
          // RELAY-CLEANUP(useHandleTemplateChange): handles Relay's open-union element variant.
          // Remove when useHandleTemplateChange leaves Relay.
          graphqlElements={selectedTemplate.data.elements}
          subject={subject}
          sessionStorageKey={usedStorageKeyPrefix}
          defaultValuesById={selectedDefaultValuesById}
          onSave={() => onCreateAction?.current?.onCreate(isSubmitting, createMore)}
          setIsFileUploading={setIsFileUploading}
          onFieldChange={isDuplicateDetectionEnabled ? handleFormFieldChangeForDuplicateDetection : onFieldChange}
        />
      )}
    </>
  )

  // Memoized so the metadata sidebar (label/assignee/project/… selectors) keeps a stable
  // element identity across title/body keystrokes. It depends only on `secondaryIssueFormData`,
  // so React can skip re-rendering this whole subtree while the user types in the title or body.
  const metadataContent = useMemo(
    () => (
      <>
        <MetadataSelectors
          ownerPinnedFields={secondaryIssueFormData?.ownerPinnedFields ?? EMPTY_ORG_PINNED_FIELDS}
          isPinnedFieldsLoading={secondaryIssueFormData === null}
          fieldVisibilityMap={secondaryIssueFormData?.fieldVisibilityMap ?? EMPTY_FIELD_VISIBILITY_MAP}
          ownerHasIssueFields={ownerHasIssueFields}
          orgHasIssueTypes={orgHasIssueTypes}
          openStatuses={openStatuses}
        />
        {secondaryIssueFormData && (
          <ContributorFooter
            supportFileUrl={secondaryIssueFormData.supportFileUrl ?? undefined}
            codeOfConductFileUrl={secondaryIssueFormData.codeOfConductFileUrl ?? undefined}
            securityPolicyUrl={secondaryIssueFormData.securityPolicyUrl ?? undefined}
            contributingFileUrl={secondaryIssueFormData.contributingFileUrl ?? undefined}
          />
        )}
      </>
    ),
    [secondaryIssueFormData, ownerHasIssueFields, orgHasIssueTypes, openStatuses],
  )

  const footerContent = showUserRestrictedView ? null : (
    <>
      {footer && (
        <div
          className={clsx(
            styles.footerArea,
            isIssueForm ? styles.footerAreaIssueForm : styles.footerAreaDefault,
            !optionConfig.insidePortal && isDuplicateDetectionEnabled && styles.duplicateDetectionStickyFooter,
          )}
        >
          {footer}
        </div>
      )}
    </>
  )

  if (showMetadataSidepanel) {
    return (
      <div className={styles.CreateIssueFormWrapper}>
        <div className={clsx('width-fit', styles.formGridContainer, styles.formGridWithMetadata)}>
          <div className={styles.mainContentSection}>{mainContent}</div>
          <div className={styles.metadataSection}>{metadataContent}</div>
          {footerContent}
        </div>
      </div>
    )
  } else {
    return (
      <>
        {mainContent}
        {metadataContent}
        {footerContent}
      </>
    )
  }
}

function formatError(message: string) {
  return new Error(`Issue create mutation failed with error: ${message}`)
}
